import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import { requestZeus } from 'components/api/zeusClient';

import { GetMarketplacePropertySellerDisclosuresDocument } from './__generated__/seller-disclosures.zeus';

export const useSellerDisclosures = (addressId?: string) => {
  const [hasSellerDisclosures, setHasSellerDisclosures] = useState(false);

  const fetchSellerDisclosures = useCallback(
    async (signal?: AbortSignal) => {
      if (!addressId) return null;

      const data = await requestZeus(
        GetMarketplacePropertySellerDisclosuresDocument,
        { addressId },
        signal,
      );
      return data.marketplace.sellerDisclosuresDownloadUrl;
    },
    [addressId],
  );

  useEffect(() => {
    if (!addressId) return;

    const controller = new AbortController();
    setHasSellerDisclosures(false);
    void fetchSellerDisclosures(controller.signal)
      .then((url) => {
        if (!controller.signal.aborted) setHasSellerDisclosures(url != null);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) Sentry.captureException(error);
      });
    return () => controller.abort();
  }, [addressId, fetchSellerDisclosures]);

  const getFreshSellerDisclosuresUrl = useCallback(async () => {
    try {
      const url = await fetchSellerDisclosures();
      if (!url) setHasSellerDisclosures(false);
      return url;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  }, [fetchSellerDisclosures]);

  return { getFreshSellerDisclosuresUrl, hasSellerDisclosures };
};
